/**
 * FloatingBookingButton Component
 * Fixed position button for opening the Calendly performance check booking dialog
 */

import { useState } from 'react';
import { CalendarDays, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  GlassModal,
  GlassModalContent,
  GlassModalHeader,
  GlassModalTitle,
  GlassModalDescription,
} from '@/components/common/GlassModal';
import { Button } from '@/components/ui/button';

const CALENDLY_URL = 'https://calendly.com/eclou/eclou-insights';

/** Routes where the floating booking button should be shown (authenticated pages) */
const ALLOWED_ROUTES = ['/dashboard', '/analysis', '/analyst'];

/**
 * Floating button with calendar icon that opens the Calendly booking dialog
 * Fixed position in the bottom-right corner, only visible on authenticated pages
 */
export function FloatingOptInButton() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { pathname } = useLocation();

  if (!ALLOWED_ROUTES.some(route => pathname.startsWith(route))) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsDialogOpen(true)}
        aria-label={t('booking.buttonAriaLabel')}
        className="
          fixed bottom-24 md:bottom-6 right-6 z-40
          flex items-center justify-center
          w-14 h-14
          rounded-full
          gradient-bg
          text-white
          shadow-lg
          transition-all duration-200
          hover:scale-110 hover:shadow-xl
          focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-start)] focus:ring-offset-2
          active:scale-95
        "
      >
        <CalendarDays className="w-6 h-6" aria-hidden="true" />
      </button>

      <GlassModal open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <GlassModalContent
          className="max-w-3xl w-full max-h-[90dvh] overflow-y-auto"
          showCloseButton={false}
          aria-describedby="booking-dialog-description"
        >
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDialogOpen(false)}
              aria-label={t('common.close')}
              className="absolute right-0 top-0 text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)]"
            >
              <X className="h-5 w-5" />
            </Button>

            <GlassModalHeader className="pr-8">
              <GlassModalTitle>{t('booking.modalTitle')}</GlassModalTitle>
              <GlassModalDescription id="booking-dialog-description">
                {t('booking.modalDescription')}
              </GlassModalDescription>
            </GlassModalHeader>

            {/* Calendly embed */}
            <div className="mt-6 rounded-[var(--radius-sm)] overflow-hidden border border-[var(--glass-border)]">
              <iframe
                src={CALENDLY_URL}
                title={t('booking.modalTitle')}
                className="w-full border-0"
                style={{ height: '660px', minHeight: '500px' }}
                loading="lazy"
              />
            </div>
          </div>
        </GlassModalContent>
      </GlassModal>
    </>
  );
}
